import { ExpenseStatusEnum, Expense } from "@finway-group/shared/lib/models"
import { Button } from "antd"
import React from "react"
import { Bell as BellIcon, X as CancelIcon, Check as CheckIcon, Upload as UploadIcon } from "react-feather"
import { useTranslation } from "react-i18next"
import { UserService } from "Shared/services"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"

type ActionButtonProp = (event: React.MouseEvent<HTMLElement>, request: Expense) => void
interface RequestActionButtonsInterface {
    request: Expense
    disabled?: boolean
    isExportButtonLoading?: boolean
    onApprove?: (event: React.MouseEvent<HTMLElement>, request: Expense) => Promise<void>
    onReject?: ActionButtonProp
    onExport?: ActionButtonProp
    onReview?: ActionButtonProp
    onUpload?: ActionButtonProp
    onSendReminder?: (event: React.MouseEvent<HTMLElement>, request: Expense) => Promise<void>
    onCancel?: ActionButtonProp
}

const RequestActionButtons: React.FC<RequestActionButtonsInterface> = ({
    request,
    disabled,
    isExportButtonLoading,
    onApprove,
    onReject,
    onExport,
    onReview,
    onUpload,
    onSendReminder,
    onCancel,
}) => {
    const { t } = useTranslation()

    const loggedInProfile = UserService.getLoggedInEmployeeProfile()
    const approver = UserService.getUserById(request.approvalNeededBy)
    const secondApprover = UserService.getUserById(request.secondApprovalNeededBy ?? "")
    const requester = UserService.getUserById(request.requestedBy)

    const isRequester = requester?.id === loggedInProfile.id
    const isApprover = (approver?.id === loggedInProfile.id || secondApprover?.id === loggedInProfile.id) && !request.approvalsForActiveWorkflowRule.includes(loggedInProfile.id);

    const [sendReminderButtonLoading, setSendReminderButtonLoading] = useStateIfMounted(false)
    const [approveButtonLoading, setApproveButtonLoading] = useStateIfMounted(false)

    const handleApprove = (event: React.MouseEvent<HTMLElement>, request: Expense) => {
        if (onApprove) {
            setApproveButtonLoading(true)
            onApprove(event, request).finally(() => setApproveButtonLoading(false))
        }
    }

    const handleSendReminder = (event: React.MouseEvent<HTMLElement>, request: Expense) => {
        if (onSendReminder) {
            setSendReminderButtonLoading(true)
            onSendReminder(event, request).finally(() => setSendReminderButtonLoading(false))
        }
    }

    switch (request.status) {
        case ExpenseStatusEnum.PURCHASE_PENDING:
        case ExpenseStatusEnum.INVOICE_PENDING:
            if (isRequester) {
                return (
                    <div className="btn-wrapper">
                        <Button className="btn-default min-w-120" type="default" icon={<CancelIcon />} onClick={!disabled ? (event) => onCancel?.(event, request) : undefined}>
                            <span>{t("action:cancel_request")}</span>
                        </Button>
                        <Button
                            className="btn-highlight-blue min-w-120"
                            icon={<BellIcon />}
                            loading={sendReminderButtonLoading}
                            onClick={!disabled ? (event) => handleSendReminder?.(event, request) : undefined}
                        >
                            <span className="m-0">{t("action:reminder")}</span>
                        </Button>
                    </div>
                )
            } else if (loggedInProfile.activeCompanyProfile?.isAdmin || isApprover) {
                return (
                    <div className="btn-wrapper">
                        <Button className="btn-default min-w-120" icon={<CancelIcon />} onClick={!disabled ? (event) => onReject?.(event, request) : undefined}>
                            <span>{t("action:reject")}</span>
                        </Button>
                        <Button className="min-w-120" loading={approveButtonLoading} type="primary" icon={<CheckIcon />} onClick={!disabled ? (event) => handleApprove(event, request) : undefined}>
                            <span>{t("action:approve")}</span>
                        </Button>
                    </div >
                )
            } else return <></>
        case ExpenseStatusEnum.DOCS_NEEDED:
            return (
                <div className="btn-wrapper">
                    <Button className="btn-highlight-blue min-w-120" icon={<UploadIcon />}>
                        <span>{t("action:invoice")}</span>
                    </Button>
                    {((loggedInProfile.activeCompanyProfile?.isAdmin && !isRequester) || isApprover) && (
                        <Button className="btn-highlight-blue min-w-120" icon={<BellIcon />} onClick={onSendReminder ? (event) => onSendReminder(event, request) : () => { }}>
                            <span className="m-0">{t("action:reminder")}</span>
                        </Button>
                    )}
                </div>
            )
        case ExpenseStatusEnum.APPROVED:
            return (
                <Button onClick={!disabled ? (event) => onReview?.(event, request) : undefined}>
                    <span>{t("action:mark_as_reviewed")}</span>
                </Button>
            )
        case ExpenseStatusEnum.REVIEWED:
        case ExpenseStatusEnum.PAID:
            return (
                <div className="btn-wrapper">
                    <Button loading={isExportButtonLoading} onClick={(event) => onExport?.(event, request)}>
                        <span>{t("action:export")}</span>
                    </Button>
                </div>
            )
        default:
            return <></>
    }
}

export default RequestActionButtons
